/**
 * Safe ErrorBoundary telemetry fields for RUM / ops logger beacons.
 *
 * Never record query strings (Auth0 invitation, organization, code, state) or
 * raw exception text — both can carry credentials or personal data.
 */

/** @typedef {typeof ERROR_BOUNDARY_ERROR_CATEGORIES[keyof typeof ERROR_BOUNDARY_ERROR_CATEGORIES]} ErrorBoundaryErrorCategory */

export const ERROR_BOUNDARY_ERROR_CATEGORIES = Object.freeze({
    TYPE_ERROR: 'type_error',
    REFERENCE_ERROR: 'reference_error',
    SYNTAX_ERROR: 'syntax_error',
    RANGE_ERROR: 'range_error',
    URI_ERROR: 'uri_error',
    EVAL_ERROR: 'eval_error',
    APPLICATION_ERROR: 'application_error',
    UNKNOWN: 'unknown',
});

const ERROR_NAME_TO_CATEGORY = Object.freeze({
    TypeError: ERROR_BOUNDARY_ERROR_CATEGORIES.TYPE_ERROR,
    ReferenceError: ERROR_BOUNDARY_ERROR_CATEGORIES.REFERENCE_ERROR,
    SyntaxError: ERROR_BOUNDARY_ERROR_CATEGORIES.SYNTAX_ERROR,
    RangeError: ERROR_BOUNDARY_ERROR_CATEGORIES.RANGE_ERROR,
    URIError: ERROR_BOUNDARY_ERROR_CATEGORIES.URI_ERROR,
    EvalError: ERROR_BOUNDARY_ERROR_CATEGORIES.EVAL_ERROR,
    Error: ERROR_BOUNDARY_ERROR_CATEGORIES.APPLICATION_ERROR,
});

/**
 * Map a caught error to an allowlisted category. Raw messages are never returned.
 *
 * @param {Error|{ name?: string }|null|undefined} error
 * @returns {ErrorBoundaryErrorCategory}
 */
export const categorizeErrorBoundaryError = (error) => {
    const name = error?.name || 'Error';
    return ERROR_NAME_TO_CATEGORY[name] || ERROR_BOUNDARY_ERROR_CATEGORIES.UNKNOWN;
};

/**
 * Pathname-only route for ErrorBoundary telemetry (no search/hash).
 *
 * @returns {string}
 */
export const getErrorBoundaryRoute = () => {
    try {
        if (typeof window === 'undefined' || !window.location) {
            return '';
        }

        return window.location.pathname || '';
    } catch {
        return '';
    }
};
